import React from 'react';
import { AlertCircle } from 'lucide-react';

/**
 * Renders Pydantic-style 422 validation errors inline inside a form.
 *
 * Each entry is the `{loc, msg}` shape emitted by
 * `e.errors(include_url=False, include_input=False)` on the backend and passed
 * through unchanged by ElectricalService's formatError. `loc` is a path array
 * (e.g. ['body', 'label']); we show the last non-"body" segment as the field
 * name so the user sees "label: field required" rather than the raw tuple.
 *
 * Renders nothing when there are no errors, so callers can drop it in
 * unconditionally.
 */
const fieldName = (loc) => {
    if (!Array.isArray(loc) || loc.length === 0) return null;
    const parts = loc.filter((p) => p !== 'body');
    return parts.length ? parts[parts.length - 1] : null;
};

const FieldErrorList = ({ errors = [] }) => {
    if (!errors.length) return null;
    return (
        <div
            role="alert"
            className="rounded-lg border border-[var(--error)] bg-[var(--error-container)] p-3 space-y-1"
        >
            {errors.map((err, i) => {
                const name = fieldName(err.loc);
                return (
                    <div key={i} className="flex items-start gap-2 text-sm text-[var(--error)]">
                        <AlertCircle size={16} className="mt-0.5 shrink-0" />
                        <span>
                            {name && <span className="font-semibold">{name}: </span>}
                            {err.msg || 'Invalid value'}
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

export default FieldErrorList;
